import * as React from "react";
import { useEffect, useState } from "react";
import styled from "styled-components";
import { useSpring, animated } from "react-spring";
import SVG from "react-inlinesvg";
import { toVWMobile } from "../../../helpers/styles";
import { BREAKPOINTS } from "../../../helpers/theme";
import CloseIcon from "../../../assets/images/close_icon.svg";
import DrawerMenuItem from "./DrawerMenuItem";
import DrawerMenuItemView from "./DrawerMenuItemView";
import * as CommonHeaderTypes from "../types";
import { NavbarOptions } from "../Header";

const clientSideWindow =
  typeof window !== "undefined"
    ? window
    : {
        innerWidth: 0,
      };

export type DrawerProps = {
  open: boolean;
  onClose: () => void;
  menuItems: Array<CommonHeaderTypes.MenuItem>;
  toggleInNav: (key: NavbarOptions) => void;
};

const Drawer = ({
  open,
  onClose,
  toggleInNav: _toggleInNav,
  menuItems,
}: DrawerProps): JSX.Element => {
  
  const styles = useSpring({
    left: !open ? -clientSideWindow.innerWidth : 0,
    config: {
      tension: 280,
      friction: 30,
      clamp: true,
    },
  });
  const [isSubMenuItemOpen, setSubMenuItemOpen] = useState<boolean>(false);
  const [selectedSubMenuItem, setSelectedSubMenuItem] =
    useState<CommonHeaderTypes.SubMenuItem>();

  useEffect(() => {
    if (open) {
      document.body.classList.add("stop-scrolling");
      document.getElementsByTagName("html")[0].classList.add("stop-scrolling");
      return () => {
        document.body.classList.remove("stop-scrolling");
        document
          .getElementsByTagName("html")[0]
          .classList.remove("stop-scrolling");
      };
    }
    if (!open) {
      setSelectedSubMenuItem(undefined);
    }
  }, [open]);

  return (
    <Container style={styles}>
      <Header>
        <Button onClick={onClose} aria-label={"Close Menu"}>
          <Icon src={CloseIcon} width={20} />
        </Button>
      </Header>
      <Content>
        {menuItems &&
          menuItems.map((mItem, index) => {
            return (
              <DrawerMenuItem
                key={`${mItem?.label || "menu-item"}-${index}`}
                menuItem={mItem}
                onClickViewMore={(sMenuItem) => {
                  setSubMenuItemOpen(true);
                  setSelectedSubMenuItem(sMenuItem);
                }}
              />
            );
          })}
        <DrawerMenuItemView
          onBackClick={() => setSubMenuItemOpen(false)}
          subMenuItem={selectedSubMenuItem}
          open={isSubMenuItemOpen}
        />
      </Content>
      <Rail side="left" />
      <Rail side="right" />
    </Container>
  );
};

const Container = styled(animated.nav)`
  width: 100vw;
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  background-color: #f6f1e7;
  z-index: 100;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    display: none;
  }
`;

const Header = styled.div`
  width: 100%;
  height: ${toVWMobile(70)}vw;
  background-color: #f6f1e7;
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  border-bottom: 1px solid #012604;
  padding: 0 ${toVWMobile(30)}vw;
`;

const Content = styled.div`
  width: 100%;
  flex: 1;
  overflow-y: auto;
  position: relative;
`;

const Button = styled.button`
  border: none;
  background-color: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${toVWMobile(30)}vw;
  height: ${toVWMobile(30)}vw;
  padding: 0;
`;

const Icon = styled(SVG)<{ width: number }>`
  width: ${(props) => toVWMobile(props.width)}vw;
  height: auto;
  object-fit: contain;
  & line {
    stroke: #012604;
  }
`;

const Rail = styled.div<{ side: "left" | "right" }>`
  position: absolute;
  top: 0;
  bottom: 0;
  ${(props) => props.side}: ${toVWMobile(15.75)}vw;
  width: 1px;
  background-color: #012604;
  z-index: 20;
  pointer-events: none;
`;

export default Drawer;
